// agendaListPdf.ts
// Generates a tabular PDF listing missions grouped by day for the agenda list view.
// Columns: Mission, Agent, Service, Heure, Durée, Récurrence
// Uses jsPDF + jspdf-autotable for clean tabular layout
// CREATED: PDF export for the list/agenda view in TeamView
// UPDATED: Removed Statut column, redistributed column widths across 6 columns
// FIXED: getRecurrenceLabel now returns "Tous les N ans" for yearly interval > 1 (matches RecurrenceBadge)
// FIXED: drawMeta removed from didDrawPage to prevent duplicate/overlapping text; only the final
//        post-pass loop draws metadata so totalPages is always accurate

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { EnrichedMission } from '../components/manager/agenda/types';
import type { Service } from './services';

type RGB = [number, number, number];

const SERVICE_PALETTES: Record<string, { bg: RGB; text: RGB }> = {
  blue:    { bg: [219, 234, 254], text: [30,  64,  175] },
  cyan:    { bg: [207, 250, 254], text: [21,  94,  117] },
  teal:    { bg: [204, 251, 241], text: [17,  94,   89] },
  lime:    { bg: [236, 252, 203], text: [63,  98,   18] },
  orange:  { bg: [255, 237, 213], text: [154,  52,  18] },
  amber:   { bg: [254, 243, 199], text: [146,  64,  14] },
  rose:    { bg: [255, 228, 230], text: [159,  18,  57] },
  pink:    { bg: [252, 231, 243], text: [157,  23,  77] },
  violet:  { bg: [237, 233, 254], text: [91,   33, 182] },
  fuchsia: { bg: [250, 232, 255], text: [134,  25, 143] },
  sky:     { bg: [224, 242, 254], text: [7,    89, 133] },
  indigo:  { bg: [224, 231, 255], text: [55,   48, 163] },
};

const C_HEADER_BG:   RGB = [26,  68,  60];
const C_HEADER_TEXT: RGB = [255, 255, 255];
const C_META_TEXT:   RGB = [107, 114, 128];
const C_FOOTER_TEXT: RGB = [156, 163, 175];
const C_FOOTER_LINE: RGB = [203, 213, 225];
const C_DAY_BG:      RGB = [248, 250, 252];
const C_DAY_TEXT:    RGB = [55,  65,  81];

const MARGIN = 10;
const HEADER_H = 16;
const META_H = 6;

async function loadLogo(url: string, timeoutMs = 5000): Promise<{ data: string; aspect: number } | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
      resolve(null);
    }, timeoutMs);
    img.onload = () => {
      clearTimeout(timer);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0);
      resolve({ data: canvas.toDataURL('image/png'), aspect: img.naturalWidth / img.naturalHeight });
    };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = url;
  });
}

function normalizeSiteSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[éèêë]/g, 'e').replace(/[àâä]/g, 'a').replace(/[ùûü]/g, 'u')
    .replace(/[îï]/g, 'i').replace(/[ôö]/g, 'o').replace(/ç/g, 'c')
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 40);
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}`;
}

function formatDayLabel(dateStr: string): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const date = new Date(y, mo - 1, d);
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
}

function getRecurrenceLabel(frequency: string | null, interval: number): string {
  if (!frequency) return '';
  if (frequency === 'daily') return interval > 1 ? `Tous les ${interval}j` : 'Quotidien';
  if (frequency === 'weekly') return interval > 1 ? `Toutes les ${interval} sem.` : 'Hebdomadaire';
  if (frequency === 'monthly') return interval > 1 ? `Tous les ${interval} mois` : 'Mensuel';
  if (frequency === 'yearly') return interval > 1 ? `Tous les ${interval} ans` : 'Annuel';
  return '';
}

export interface GenerateAgendaListPdfOptions {
  siteName: string;
  periodLabel: string;
  tasks: EnrichedMission[];
  services: Service[];
}

export async function generateAgendaListPdf(options: GenerateAgendaListPdfOptions): Promise<void> {
  const { siteName, periodLabel, tasks, services } = options;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - 2 * MARGIN;

  const logo = await loadLogo('/logo-tasq-fh-blanc.png');

  function drawHeader() {
    doc.setFillColor(...C_HEADER_BG);
    doc.rect(0, 0, pageW, HEADER_H, 'F');

    if (logo) {
      const logoH = HEADER_H - 4;
      const logoW = logoH * logo.aspect;
      doc.addImage(logo.data, 'PNG', MARGIN, (HEADER_H - logoH) / 2, logoW, logoH);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...C_HEADER_TEXT);
    doc.text('Liste des Missions', pageW - MARGIN, HEADER_H / 2 + 1, { align: 'right', baseline: 'middle' });
  }

  function drawMeta(currentPage: number, totalPages: number) {
    const y = HEADER_H + 1;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C_META_TEXT);

    const leftLabel = siteName ? `${siteName} · ${periodLabel}` : periodLabel;
    doc.text(leftLabel, MARGIN, y + META_H / 2, { baseline: 'middle' });

    const now = new Date();
    const genDate = now.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.text(`Généré le ${genDate}  ·  Page ${currentPage}/${totalPages}`, pageW - MARGIN, y + META_H / 2, { align: 'right', baseline: 'middle' });
  }

  function drawFooter() {
    const y = pageH - 6;
    doc.setDrawColor(...C_FOOTER_LINE);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y - 1, pageW - MARGIN, y - 1);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C_FOOTER_TEXT);
    doc.text('TASQ — Liste des Missions', MARGIN, y + 1.5, { baseline: 'middle' });
    if (siteName) {
      doc.text(siteName, pageW / 2, y + 1.5, { align: 'center', baseline: 'middle' });
    }
    doc.text(periodLabel, pageW - MARGIN, y + 1.5, { align: 'right', baseline: 'middle' });
  }

  const tasksByDate = new Map<string, EnrichedMission[]>();
  tasks.forEach(task => {
    const key = task.assigned_date;
    const existing = tasksByDate.get(key) || [];
    existing.push(task);
    tasksByDate.set(key, existing);
  });

  const sortedDates = Array.from(tasksByDate.keys()).sort();
  sortedDates.forEach(dateStr => {
    tasksByDate.set(dateStr, (tasksByDate.get(dateStr) || []).sort((a, b) => {
      if (!a.scheduled_time && !b.scheduled_time) return 0;
      if (!a.scheduled_time) return 1;
      if (!b.scheduled_time) return -1;
      return a.scheduled_time.localeCompare(b.scheduled_time);
    }));
  });

  type CellDef = { content: string; colSpan?: number; styles?: Record<string, unknown> };
  type TableRow = (string | CellDef)[];

  const body: TableRow[] = [];
  const dayRowIndices = new Set<number>();
  const rowServiceColors: (string | undefined)[] = [];

  sortedDates.forEach(dateStr => {
    const dayMissions = tasksByDate.get(dateStr) || [];
    dayRowIndices.add(body.length);
    rowServiceColors.push(undefined);
    body.push([{
      content: formatDayLabel(dateStr).toUpperCase(),
      colSpan: 6,
      styles: {
        fillColor: C_DAY_BG,
        textColor: C_DAY_TEXT,
        fontStyle: 'bold',
        fontSize: 7.5,
        cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 },
      },
    }]);

    dayMissions.forEach(task => {
      const service = services.find(s => s.id === task.template_service_id);
      const operatorName = task.assigned_to_user?.full_name || '—';
      const duration = task.duration_override_minutes ?? task.template.estimated_duration_minutes;
      const time = task.scheduled_time ? task.scheduled_time.slice(0, 5) : '—';
      rowServiceColors.push(service?.color);
      body.push([
        task.template.name,
        operatorName,
        service?.name || '—',
        time,
        formatDuration(duration),
        getRecurrenceLabel(task.recurrenceFrequency, task.recurrenceInterval),
      ]);
    });
  });

  const startY = HEADER_H + META_H + 2;

  drawHeader();

  autoTable(doc, {
    head: [['Mission', 'Agent', 'Service', 'Heure', 'Durée', 'Récurrence']],
    body,
    startY,
    margin: { left: MARGIN, right: MARGIN },
    tableWidth: contentW,
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 45 },
      2: { cellWidth: 25 },
      3: { cellWidth: 16 },
      4: { cellWidth: 14 },
      5: { cellWidth: 20 },
    },
    headStyles: {
      fillColor: [44, 104, 96],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
      cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
    },
    bodyStyles: {
      fontSize: 7.5,
      cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
      lineColor: [229, 231, 235],
      lineWidth: 0.2,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const rowIdx = data.row.index;
        if (dayRowIndices.has(rowIdx)) {
          return;
        }
        if (data.column.index === 2) {
          const color = rowServiceColors[rowIdx];
          if (color && SERVICE_PALETTES[color]) {
            data.cell.styles.fillColor = SERVICE_PALETTES[color].bg;
            data.cell.styles.textColor = SERVICE_PALETTES[color].text;
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    },
    didDrawPage: (data) => {
      drawHeader();
      drawFooter();
      if (data.settings.margin && typeof data.settings.margin === 'object' && 'top' in data.settings.margin) {
        (data.settings.margin as { top: number }).top = startY;
      }
    },
  });

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawMeta(p, totalPages);
  }

  const siteSlug = normalizeSiteSlug(siteName || 'equipe');
  const periodSlug = periodLabel
    .toLowerCase()
    .replace(/[éèêë]/g, 'e').replace(/[àâä]/g, 'a').replace(/[ùûü]/g, 'u')
    .replace(/[îï]/g, 'i').replace(/[ôö]/g, 'o').replace(/ç/g, 'c')
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 40);
  doc.save(`liste-missions-${siteSlug}-${periodSlug}.pdf`);
}
